/**
 * Eva per-company monthly budget-override API client (SF-3737).
 *
 * Wraps the admin budget-override endpoints on sf-api so the company-detail
 * page can READ the effective monthly cap and WRITE (upsert / revert) the
 * per-company override without re-deriving paths or response types inline.
 * Mirrors the data-access pattern in evaUsageClient.ts.
 *
 *   GET /admin/eva/budget-override/:companyId  — perm admin:eva:budget:read
 *   PUT /admin/eva/budget-override/:companyId  — perm admin:eva:budget:write
 *     body { monthlyCostLimitUsd: number|null, note?: string }
 *     number → upsert override; null → revert to env default.
 *
 * ⚠️ sf-api wraps every response in `{ success, data }`. The Refine
 * `dataProvider.custom` unwraps the sf-api envelope and returns `{ data:
 * innerPayload }`, so `useCustom` exposes the inner payload via
 * `query.data?.data` (same convention as the usage hooks). Money values are
 * fixed-precision STRINGS to avoid float drift — parse only at the edge.
 */
import { useCustom, useCustomMutation } from '@refinedev/core';

/** Base path for the Eva budget-override endpoints on sf-api. */
export const EVA_BUDGET_BASE = 'admin/eva/budget-override';

/** Permission gating budget reads (matches sf-api requirePermissions). */
export const EVA_BUDGET_READ_PERMISSION = 'admin:eva:budget:read';

/** Permission gating budget writes (matches sf-api requirePermissions). */
export const EVA_BUDGET_WRITE_PERMISSION = 'admin:eva:budget:write';

/**
 * Effective-cap envelope returned by GET/PUT (mirrors sf-api
 * services/insights/evaBudgetOverrideService.js `shapeOverride`).
 *
 * - `effectiveCapUsd`: the cap actually enforced — the override value when one
 *   is set, else the configured env default. Always a string.
 * - `monthlyCostLimitUsd`: the override value as a string when an override
 *   exists, else null (no per-company override → running on the default).
 * - `isOverride`: whether a per-company override doc currently exists.
 */
export interface EvaBudgetOverride {
  companyId: string;
  monthlyCostLimitUsd: string | null;
  effectiveCapUsd: string;
  isOverride: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
  note: string | null;
}

/** Body for PUT — a number upserts the override, null reverts to the default. */
export interface EvaBudgetUpdate {
  monthlyCostLimitUsd: number | null;
  note?: string;
}

/** Common options shared by the budget hooks. */
interface EnabledOption {
  /** Gate the request (e.g. on access-control resolution). Defaults to true. */
  enabled?: boolean;
}

/**
 * Fetch a company's effective monthly Eva cap + override state.
 * Disabled when `companyId` is empty so the page can mount before an id is
 * available without firing a request. Returns the inner payload via
 * `query.data?.data` (sf-api envelope unwrapped by the data provider).
 * @param companyId - company ObjectId
 */
export function useCompanyBudgetOverride(companyId: string, options: EnabledOption = {}) {
  return useCustom<EvaBudgetOverride>({
    url: `/${EVA_BUDGET_BASE}/${companyId}`,
    method: 'get',
    queryOptions: {
      enabled: (options.enabled ?? true) && !!companyId,
      // Don't refetch on window focus: a background refetch re-seeds the edit
      // field via the effect below and would silently discard an operator's
      // unsaved cap edit when they tab away and back. The value only needs to
      // (re)load on mount and after an explicit save (manual refetch).
      refetchOnWindowFocus: false,
    },
    // The page surfaces budget-read failures inline (and a missing read perm is
    // a 403), so suppress the global error toast for this supplementary query.
    errorNotification: false,
  });
}

/**
 * Mutation to upsert (number) or revert (null) a company's monthly cap.
 * The caller supplies the companyId + body at call time; on success the page
 * refetches so the "% of Monthly Cap" figures reflect the new cap. Errors
 * (including a 403 for callers lacking `admin:eva:budget:write`) are surfaced
 * by the caller, so the global error toast is suppressed here.
 */
export function useSetCompanyBudgetOverride() {
  return useCustomMutation<EvaBudgetOverride>();
}

/**
 * Build the PUT mutation args for a budget-override write. Extracted so callers
 * and tests share one source of truth for the URL + method + body shape.
 * @param companyId - company ObjectId
 * @param body - the update payload (number upserts, null reverts)
 */
export function budgetUpdateMutationArgs(companyId: string, body: EvaBudgetUpdate) {
  return {
    url: `/${EVA_BUDGET_BASE}/${companyId}`,
    method: 'put' as const,
    values: body,
    // Failures are surfaced inline by the control (esp. 403); no global toast.
    errorNotification: false as const,
    successNotification: false as const,
  };
}
